import React, { useState, useEffect } from 'react';
import api from '../../services/api';

interface Product {
  id: number;
  name: string;
  serialized: boolean;
  stock_quantity: number;
  tracking_types: string[];
}

interface Unit {
  id: number;
  identifier_values: { [key: string]: string };
  status: string;
}

interface Batch {
  id: number;
  batch_number: string;
  remaining_quantity: number;
}

interface StockAdjustmentModalProps {
  product: Product;
  onClose: () => void;
  onSuccess: () => void;
}

const REASONS = [
  { value: 'damaged', label: 'Damaged' },
  { value: 'defective', label: 'Defective' },
  { value: 'expired', label: 'Expired' },
  { value: 'lost', label: 'Lost' },
  { value: 'broken', label: 'Broken' },
  { value: 'correction', label: 'Stock Correction' },
  { value: 'other', label: 'Other' }
];

const StockAdjustmentModal: React.FC<StockAdjustmentModalProps> = ({ product, onClose, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [batches, setBatches] = useState<Batch[]>([]);
  const [locations, setLocations] = useState<any[]>([]);
  const [vendors, setVendors] = useState<any[]>([]);
  const [availableUnits, setAvailableUnits] = useState<Unit[]>([]);
  const [selectedUnitIds, setSelectedUnitIds] = useState<number[]>([]);
  
  const [formData, setFormData] = useState({
    quantity: 1,
    reason: 'damaged',
    stock_batch_id: '',
    location_id: '',
    vendor_id: '',
    notes: '',
    adjustment_date: new Date().toISOString().split('T')[0]
  });
 
  const [error, setError] = useState<string | null>(null);
 
  useEffect(() => {
    loadBatches();
    loadLocations();
    loadVendors();
    if (product.serialized) {
      loadAvailableUnits();
    }
  }, [product.id]);
 
  const loadBatches = async () => {
    try {
      const resp = await api.get(`/products/${product.id}/batches`);
      setBatches(resp.data?.batches || []);
    } catch (e) {
      console.error("Failed to load batches", e);
    }
  };

  const loadLocations = async () => {
    try {
      const resp = await api.get('/locations');
      const locs = resp.data?.data || [];
      setLocations(locs);
      
      // Auto-select primary location if available
      const primaryLoc = locs.find((l: any) => l.is_primary) || locs[0];
      if (primaryLoc) {
        setFormData(f => ({ ...f, location_id: primaryLoc.id }));
      }
    } catch (e) {
      console.error("Failed to load locations", e);
    }
  };

  const loadVendors = async () => {
    try {
      const resp = await api.get('/vendors?active=true');
      const data = resp.data?.data || [];
      setVendors(data);
    } catch (e) {
      console.error("Failed to load vendors", e);
    }
  };
 
  const loadAvailableUnits = async () => {
    setLoading(true);
    try {
      const resp = await api.get(`/products/${product.id}/units/available`);
      setAvailableUnits(resp.data?.units || []);
    } catch (e) {
      console.error("Failed to load units", e);
    } finally {
      setLoading(false);
    }
  };
 
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    setSubmitting(true);
    setError(null);
 
    const payload = {
      ...formData,
      product_id: product.id,
      quantity: product.serialized ? selectedUnitIds.length : formData.quantity,
      product_unit_ids: selectedUnitIds
    };
 
    if (!product.serialized && payload.quantity <= 0) {
      setError("Quantity must be greater than 0");
      setSubmitting(false);
      return;
    }
 
    if (product.serialized && selectedUnitIds.length === 0) {
      setError("Please select at least one unit to adjust");
      setSubmitting(false);
      return;
    }
 
    try {
      const resp = await api.post('/stock_adjustments', { stock_adjustment: payload });
      if (resp.success) {
        onSuccess();
        onClose();
      } else {
        setError(resp.message || resp.errors?.join(', ') || "Failed to save adjustment");
      }
    } catch (e: any) {
      setError(e.message || "An unexpected error occurred");
    } finally {
      setSubmitting(false);
    }
  };
 
  const toggleUnit = (unitId: number) => {
    setSelectedUnitIds(prev => 
      prev.includes(unitId) ? prev.filter(id => id !== unitId) : [...prev, unitId]
    );
  };
 
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-in fade-in duration-300">
      <div className="bg-white w-full max-w-2xl rounded-[2rem] shadow-2xl overflow-hidden border border-slate-100 flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="p-8 bg-slate-50 border-b border-slate-100 flex justify-between items-center">
          <div>
            <h2 className="text-2xl font-black text-slate-900 tracking-tight">Adjust Inventory</h2>
            <p className="text-slate-500 font-bold text-sm uppercase tracking-widest">{product.name}</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-white rounded-full transition-colors text-slate-400 hover:text-rose-500 shadow-sm border border-transparent hover:border-slate-100">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-10 space-y-8">
          {error && (
            <div className="p-4 bg-rose-50 border-l-4 border-rose-500 rounded-xl flex items-center gap-3 animate-bounce">
              <svg className="w-5 h-5 text-rose-500 shrink-0" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" /></svg>
              <p className="text-sm text-rose-800 font-bold">{error}</p>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="space-y-2">
              <label className="text-xs font-black text-slate-400 uppercase tracking-widest pl-1">Reason for Adjustment</label>
              <select
                value={formData.reason}
                onChange={e => setFormData(f => ({ ...f, reason: e.target.value }))}
                className="w-full px-5 py-4 bg-slate-50 border-2 border-slate-100 rounded-2xl focus:ring-4 focus:ring-indigo-500/10 focus:border-indigo-500 outline-none transition-all font-bold text-slate-700"
              >
                {REASONS.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
              </select>
            </div>

            <div className="space-y-2">
              <label className="text-xs font-black text-slate-400 uppercase tracking-widest pl-1">Adjustment Date</label>
              <input
                type="date"
                value={formData.adjustment_date}
                onChange={e => setFormData(f => ({ ...f, adjustment_date: e.target.value }))}
                className="w-full px-5 py-4 bg-slate-50 border-2 border-slate-100 rounded-2xl focus:ring-4 focus:ring-indigo-500/10 focus:border-indigo-500 outline-none transition-all font-bold text-slate-700"
              />
            </div>
          </div>

          {!product.serialized ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="space-y-2">
                <label className="text-xs font-black text-slate-400 uppercase tracking-widest pl-1">Quantity to Remove</label>
                <div className="relative">
                  <input
                    type="number"
                    value={formData.quantity}
                    onChange={e => setFormData(f => ({ ...f, quantity: Number(e.target.value) }))}
                    className="w-full px-5 py-4 bg-slate-50 border-2 border-slate-100 rounded-2xl focus:ring-4 focus:ring-indigo-500/10 focus:border-indigo-500 outline-none transition-all font-black text-lg"
                    min="1"
                    required
                  />
                  <span className="absolute right-5 top-1/2 -translate-y-1/2 font-bold text-slate-300 uppercase text-[10px] tracking-tighter">Units</span>
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-xs font-black text-slate-400 uppercase tracking-widest pl-1">Target Location</label>
                <select
                  value={formData.location_id}
                  onChange={e => setFormData(f => ({ ...f, location_id: e.target.value }))}
                  className="w-full px-5 py-4 bg-slate-50 border-2 border-slate-100 rounded-2xl focus:ring-4 focus:ring-indigo-500/10 focus:border-indigo-500 outline-none transition-all font-bold text-slate-700"
                >
                  <option value="">Select Location (Optional)</option>
                  {locations.map(loc => (
                    <option key={loc.id} value={loc.id}>
                      {loc.name} {loc.is_primary ? '(Primary)' : ''}
                    </option>
                  ))}
                </select>
              </div>

              <div className="space-y-2">
                <label className="text-xs font-black text-slate-400 uppercase tracking-widest pl-1">Select Vendor</label>
                <select
                  value={formData.vendor_id}
                  onChange={e => setFormData(f => ({ ...f, vendor_id: e.target.value }))}
                  className="w-full px-5 py-4 bg-slate-50 border-2 border-slate-100 rounded-2xl focus:ring-4 focus:ring-indigo-500/10 focus:border-indigo-500 outline-none transition-all font-bold text-slate-700"
                >
                  <option value="">Select Vendor (Optional)</option>
                  {vendors.map(v => (
                    <option key={v.id} value={v.id}>
                      {v.name} {v.phone ? `(${v.phone})` : ''}
                    </option>
                  ))}
                </select>
              </div>

              {batches.length > 0 && (
                <div className="space-y-2">
                  <label className="text-xs font-black text-slate-400 uppercase tracking-widest pl-1">Select Batch (Optional)</label>
                  <select
                    value={formData.stock_batch_id}
                    onChange={e => setFormData(f => ({ ...f, stock_batch_id: e.target.value }))}
                    className="w-full px-5 py-4 bg-slate-50 border-2 border-slate-100 rounded-2xl focus:ring-4 focus:ring-indigo-500/10 focus:border-indigo-500 outline-none transition-all font-bold text-slate-700 font-mono"
                  >
                    <option value="">Global Stock (No Batch)</option>
                    {batches.map(b => (
                      <option key={b.id} value={b.id}>
                        {b.batch_number} ({b.remaining_quantity} available)
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="space-y-2">
                  <label className="text-xs font-black text-slate-400 uppercase tracking-widest pl-1">Source Location</label>
                  <select
                    value={formData.location_id}
                    onChange={e => setFormData(f => ({ ...f, location_id: e.target.value }))}
                    className="w-full px-5 py-4 bg-slate-50 border-2 border-slate-100 rounded-2xl focus:ring-4 focus:ring-indigo-500/10 focus:border-indigo-500 outline-none transition-all font-bold text-slate-700"
                  >
                    <option value="">Select Location (Optional)</option>
                    {locations.map(loc => (
                      <option key={loc.id} value={loc.id}>
                        {loc.name} {loc.is_primary ? '(Primary)' : ''}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="space-y-2">
                  <label className="text-xs font-black text-slate-400 uppercase tracking-widest pl-1">Select Vendor</label>
                  <select
                    value={formData.vendor_id}
                    onChange={e => setFormData(f => ({ ...f, vendor_id: e.target.value }))}
                    className="w-full px-5 py-4 bg-slate-50 border-2 border-slate-100 rounded-2xl focus:ring-4 focus:ring-indigo-500/10 focus:border-indigo-500 outline-none transition-all font-bold text-slate-700"
                  >
                    <option value="">Select Vendor (Optional)</option>
                    {vendors.map(v => (
                      <option key={v.id} value={v.id}>
                        {v.name} {v.phone ? `(${v.phone})` : ''}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="space-y-4">
              <div className="flex items-center justify-between">
                <label className="text-xs font-black text-slate-400 uppercase tracking-widest pl-1">
                  Select Specific {product.tracking_types?.[0] || 'Unit'}s
                </label>
                <span className="text-[10px] font-black bg-indigo-50 text-indigo-600 px-3 py-1 rounded-full uppercase tracking-widest">
                  {selectedUnitIds.length} Selected
                </span>
              </div>
              
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 p-6 bg-slate-50 rounded-[1.5rem] border border-slate-100 max-h-60 overflow-y-auto">
                {availableUnits.map(unit => {
                  const idValue = Object.values(unit.identifier_values)[0];
                  const isSelected = selectedUnitIds.includes(unit.id);
                  return (
                    <button
                      key={unit.id}
                      type="button"
                      onClick={() => toggleUnit(unit.id)}
                      className={`px-4 py-3 rounded-xl border-2 transition-all text-xs font-bold text-left flex items-center justify-between group ${
                        isSelected 
                        ? 'bg-indigo-600 border-indigo-600 text-white shadow-lg shadow-indigo-100' 
                        : 'bg-white border-slate-100 text-slate-600 hover:border-indigo-200'
                      }`}
                    >
                      <span className="truncate">{idValue}</span>
                      {isSelected && (
                        <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" /></svg>
                      )}
                    </button>
                  );
                })}
                {availableUnits.length === 0 && !loading && (
                  <div className="col-span-full py-8 text-center text-slate-400 font-bold text-sm">
                    No available units found for adjustment.
                  </div>
                )}
                {loading && (
                  <div className="col-span-full py-8 flex justify-center">
                    <div className="animate-spin h-6 w-6 border-2 border-indigo-500 border-t-transparent rounded-full"></div>
                  </div>
                )}
              </div>
            </div>
          </div>
          )}

          <div className="space-y-2">
            <label className="text-xs font-black text-slate-400 uppercase tracking-widest pl-1">Notes / Internal Reference</label>
            <textarea
              value={formData.notes}
              onChange={e => setFormData(f => ({ ...f, notes: e.target.value }))}
              className="w-full px-5 py-4 bg-slate-50 border-2 border-slate-100 rounded-2xl focus:ring-4 focus:ring-indigo-500/10 focus:border-indigo-500 outline-none transition-all font-medium resize-none"
              rows={3}
              placeholder="e.g. Broken during unloading from truck..."
            />
          </div>
        </form>

        {/* Footer */}
        <div className="p-8 bg-slate-50 border-t border-slate-100 flex gap-4">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 py-4 text-sm font-black text-slate-500 hover:text-slate-800 transition-all uppercase tracking-widest"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting}
            className={`flex-[2] py-4 bg-indigo-600 text-white rounded-2xl text-sm font-black uppercase tracking-[0.2em] shadow-xl shadow-indigo-100 hover:bg-indigo-700 hover:-translate-y-1 active:scale-95 transition-all flex items-center justify-center gap-3 ${submitting ? 'opacity-70 cursor-not-allowed' : ''}`}
          >
            {submitting ? (
              <>
                <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                Updating...
              </>
            ) : (
              'Save Adjustment'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default StockAdjustmentModal;
